import { SocialLogin } from "@capgo/capacitor-social-login";

// window.google is already declared (as `any`) by GoogleMap.tsx for the
// Maps JS API — Identity Services hangs off the same global object, so
// callers here just narrow it locally instead of re-declaring the global.
export interface GoogleIdentityId {
  initialize: (config: { client_id: string; callback: (resp: { credential: string }) => void }) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
}

export function googleIdentityId(): GoogleIdentityId | undefined {
  return (window as any).google?.accounts?.id;
}

// Same lazy-script-tag pattern as loadRazorpayCheckout — Google Identity
// Services isn't npm-installable as a browser bundle, and it's only worth
// fetching once the login screen actually knows a client ID is configured.
let loadPromise: Promise<void> | null = null;
export function loadGoogleIdentity(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load Google Sign-In"));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

// The ID token is a JWT — decoding its payload client-side (no signature
// check needed here, the server independently verifies it) just to prefill
// name/email when a Google sign-in doesn't match an existing account.
export function decodeGoogleCredential(credential: string): { email: string; name: string } | null {
  try {
    const payload = credential.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const data = JSON.parse(json);
    return { email: data.email ?? "", name: data.name ?? data.given_name ?? "" };
  } catch {
    return null;
  }
}

// Native Google Sign-In (Android) — Google Identity Services' web SDK above
// doesn't work inside the Capacitor app's WebView, so the native app uses
// Android's Credential Manager instead, via this plugin. It needs the same
// *web* OAuth client ID passed as webClientId (not an Android client ID —
// the Android client is matched implicitly by package name + signing
// certificate, registered in Google Cloud Console, never passed here).
let nativeInitialized: string | null = null;
async function ensureNativeInitialized(webClientId: string): Promise<void> {
  if (nativeInitialized === webClientId) return;
  await SocialLogin.initialize({ google: { webClientId, mode: "online" } });
  nativeInitialized = webClientId;
}

// Returns the ID token on success, or null if the user cancelled the
// native account picker — anything else (misconfiguration, no Google
// account on the device) throws, same as the web flow's own failure modes.
//
// Deliberately doesn't pass a custom `scopes` option: the plugin's Android
// side silently rejects any login call carrying custom scopes unless the
// app's MainActivity implements its own ModifiedMainActivityForSocialLoginPlugin
// interface (ours doesn't, and doesn't need to — the default scopes it
// already requests, openid/email/profile, are exactly what the ID token
// needs for our backend's verifyIdToken()). That rejection never logs
// anything to Logcat, which made it genuinely invisible during testing —
// found by reading the plugin's own Java source, not from any error output.
export async function signInWithGoogleNative(webClientId: string): Promise<string | null> {
  await ensureNativeInitialized(webClientId);
  try {
    const { result } = await SocialLogin.login({ provider: "google", options: {} });
    return "idToken" in result ? result.idToken : null;
  } catch (err: any) {
    if (err?.code === "USER_CANCELLED") return null;
    throw err;
  }
}
